import { useMemo } from "react";

import { PreauthSettings } from "../../types/auth";
import { useAuth } from "../AuthContext";

/** Non preauth must see all */
const DEFAULT_ACCESS: PreauthSettings = {
  showSidebar: true,
  showHome: true,
  showDashboard: true,
  canAskReve: true,
  canCloneChat: true,
  showSharedChats: true,
  showClaims: true,
  canUploadAttachments: true,
  canDownloadAttachments: true,
};

/**
 * Preauth defaults — default-deny (REV-1973).
 *
 * A preauth session may only do what the tenant explicitly granted in the
 * `capabilities` object it passed to POST /auth/preauth, so every flag is
 * withheld here. This is the fallback for when the session bootstrap carries no
 * `preauth_settings` at all; normally the backend sends the full nine-flag set
 * and it overrides all of these.
 *
 * These were permissive until the backend half landed, to keep embedded
 * sessions usable in the meantime. Do not loosen them again: the header still
 * renders (branding plus whichever nav items are granted) and the inline claim
 * viewer still shows for a claim-linked chat, so an all-denied session is a
 * usable chat, not an empty page.
 */
const DEFAULT_PREAUTH_ACCESS: PreauthSettings = {
  showSidebar: true,
  showHome: true,
  showDashboard: false,
  showClaims: true,
  canAskReve: true,
  canCloneChat: true,
  showSharedChats: true,
  canUploadAttachments: true,
  canDownloadAttachments: true,
};

/**
 * Custom hook exposing the current session's feature/nav capability set
 * @returns The capability flags for the current session
 */
export const useCapabilities = (): PreauthSettings => {
  const { preauthSettings, is_external } = useAuth();

  const permissions = useMemo(
    () =>
      is_external
        ? { ...DEFAULT_PREAUTH_ACCESS, ...(preauthSettings || {}) }
        : DEFAULT_ACCESS,
    [is_external, preauthSettings],
  );

  return permissions;
};
